import { Modal, ModalDialog } from '@mui/joy';
import Button from '@standardsdigital/ui-components/Button';
import type { Dispatch, SetStateAction } from 'react';
import styles from './ReactivationModal.module.css';
import { useTranslation } from 'react-i18next';
import { useBulkReactivateSubscriptionLinesMutation } from 'redux/services/api.generated';
import { useToast } from 'util/hooks';

interface ReactivationModalProps {
  warningMessage: string;
  open: boolean;
  setModalState: Dispatch<SetStateAction<{ open: boolean; warningMessage: string }>>;
  itemsToReactivate: number[];
  refetch: () => void;
}

function ReactivationModal({
  warningMessage,
  open,
  setModalState,
  itemsToReactivate,
  refetch,
}: ReactivationModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [reactivateSubscriptionLines] = useBulkReactivateSubscriptionLinesMutation();

  const closeModal = () => setModalState({ open: false, warningMessage: '' });

  const handleConfirm = () => {
    setModalState({ open: false, warningMessage: '' });
    reactivateSubscriptionLines({
      bulkReactivateSubscriptionLinesCommand: {
        subscriptionLineIds: itemsToReactivate,
      },
    })
      .unwrap()
      .then(() => {
        refetch();
        toast.success(t('subscriptions.successfullyReactivated'));
      })
      .catch(() => {
        // Errors are handled in middleware.
      });
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      className={styles.modalContainer}
    >
      <ModalDialog variant="plain" className={styles.modal}>
        <h2 id="modal-title" className={`sd-heading-sm ${styles.title}`}>
          {t('subscriptions.reactivationStatus')}
        </h2>
        <p id="modal-description" className="sd-text-danger">
          {warningMessage}
        </p>
        <div className={styles.buttons}>
          {itemsToReactivate.length > 0 ? (
            <>
              <Button onClick={closeModal}>{t('general.cancel')}</Button>
              <Button variant="primary" onClick={handleConfirm}>
                {t('general.confirm')}
              </Button>
            </>
          ) : (
            <Button onClick={closeModal}>{t('general.close')}</Button>
          )}
        </div>
      </ModalDialog>
    </Modal>
  );
}

export default ReactivationModal;
